import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useAxios } from './use-axios';

const API_BASE_URL = '/domains/search';

export const RELATED_DOMAINS_QUERY_KEY = ['related-domains'];

interface ApiError {
  error?: {
    message?: string;
    status?: number;
  };
  details?: Array<{ message: string }>;
}

function getErrorMessage(error: unknown): string {
  if (isAxiosError<ApiError>(error)) {
    if (error.response?.data?.details) {
      return 'Please check your input and try again';
    }

    if (error.response?.status === 500) {
      return 'An internal server error occurred. Please try again later';
    }

    if (error.response?.status === 401 || error.response?.status === 403) {
      return 'You do not have permission to perform this action';
    }

    if (error.response?.status === 404) {
      return 'The requested resource was not found';
    }

    if (error.message === 'Network Error') {
      return 'Unable to connect to the server. Please check your internet connection';
    }
  }

  return 'An unexpected error occurred. Please try again';
}

export interface RelatedDomain {
  name: string;
  organization_legal_name: string | null;
  licenses: string | null;
  last_verified: string | null;
  most_recent_registrar: string | null;
}

interface SearchParams {
  emailDomains?: string[];
  licenses?: string[]; // format: "authority,value"
}

/**
 * Hook to search for domains related to a deal by registrant email domain and/or licenses.
 *
 * @param params - Search parameters containing emailDomains and/or licenses
 * @param enabled - Whether to enable the query (default: true)
 */
export function useRelatedDomains(params: SearchParams, enabled = true) {
  const axios = useAxios();

  const hasSearchParams = (params.emailDomains && params.emailDomains.length > 0)
    || (params.licenses && params.licenses.length > 0);

  return useQuery({
    queryKey: [...RELATED_DOMAINS_QUERY_KEY, params],
    queryFn: async () => {
      try {
        const { data } = await axios.post<RelatedDomain[]>(API_BASE_URL, params);
        return data;
      } catch (error) {
        throw new Error(getErrorMessage(error));
      }
    },
    enabled: enabled && hasSearchParams,
    // Keep data cached for 5 minutes since it doesn't change often
    staleTime: 5 * 60 * 1000,
    // Disable automatic refetching since this is a targeted search
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
}
